import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, ArrowRight, BookOpenCheck, FileSearch, Users, GitBranch, FileText, Cpu, Layers, AlertTriangle, ScanSearch, UserCheck, MessageSquareWarning, PenLine, Database, FlaskConical, Globe2, HeartPulse, Newspaper, BarChart3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { fade, Eyebrow, PageHeader, usePageMeta } from "@/components/SiteLayout";

const EVIDENCE_SOURCES = [
  { icon: FileText, title: "Your evidence package", desc: "Clinical study reports, trial data, protocols and internal analyses — handled under strict confidentiality." },
  { icon: FlaskConical, title: "Published clinical evidence", desc: "Pivotal trials, comparator studies and the scientific literature relevant to your asset and indication." },
  { icon: BarChart3, title: "HTA assessments & precedents", desc: "Published JCA reports and national HTA appraisals — the closest available signal of how assessors will judge your evidence." },
  { icon: Database, title: "Registries & real-world evidence", desc: "Disease registries, natural-history data and RWE sources that contextualize your comparative position." },
  { icon: HeartPulse, title: "Patient evidence", desc: "Quality-of-life, patient-reported outcomes, caregiver and preference evidence — increasingly decisive in JCA." },
  { icon: Globe2, title: "Country & competitor landscape", desc: "National treatment pathways, comparator usage and competitor evidence strategies across major EU markets." },
];

const GUIDANCE = [
  "EU Joint Clinical Assessment Regulation (HTAR)",
  "Official JCA methodological and procedural guidance",
  "EUnetHTA 21 methodology",
  "EMA EPARs and regulatory assessments",
  "National HTA methodologies — IQWiG, HAS, AIFA, ZIN and others",
  "Published assessor decisions and expectations",
];

const AI_SUPPORT = [
  { icon: ScanSearch, title: "Extraction at scale", desc: "AI reads and extracts evidence from thousands of pages — trials, reports, guidance and precedents — in hours, not months." },
  { icon: Layers, title: "Structuring against JCA requirements", desc: "Extracted evidence is organized against the questions the JCA framework actually asks, so nothing relevant is overlooked." },
  { icon: Newspaper, title: "Precedent comparison", desc: "Your evidence position is compared against published assessments and assessor behaviour to anticipate likely challenges." },
  { icon: AlertTriangle, title: "Risk surfacing", desc: "Gaps, inconsistencies and vulnerabilities are flagged and prioritized — giving consultants a complete risk picture to interrogate." },
];

const VALIDATION_STEPS = [
  { icon: UserCheck, title: "Consultant review of key findings", desc: "AI-surfaced findings are triaged, and those that shape the recommendation are reviewed by experienced HEOR consultants. What doesn't withstand scrutiny is corrected or discarded." },
  { icon: MessageSquareWarning, title: "Interpretation through an assessor's lens", desc: "Findings are judged against published assessments, national methods and our HTA experience — how is this likely to be received, and where will it be challenged?" },
  { icon: GitBranch, title: "Challenge and refinement", desc: "Conclusions are pressure-tested internally before they reach you: alternative explanations considered, weak claims removed, uncertainty stated openly." },
  { icon: PenLine, title: "Accountable sign-off", desc: "Every deliverable is approved by a named senior consultant — who presents it to your team and stands behind its conclusions." },
];

const TRACE_CHAIN = [
  { icon: FileText, label: "Supporting evidence", desc: "The document, trial or dataset behind the claim" },
  { icon: FileSearch, label: "Source & analysis", desc: "Which analysis produced it, from which source, when" },
  { icon: GitBranch, label: "Documented rationale", desc: "Why we concluded what we concluded — in writing" },
  { icon: Users, label: "Expert validation", desc: "Who reviewed and approved it, and their assessment" },
];

const SIGNALS = ["AI-assisted", "Consultant-validated", "Evidence-traceable", "Built around published JCA guidance", "Board-ready", "Confidential engagement"];

export default function Trust() {
  const navigate = useNavigate();
  usePageMeta(
    "Trust & Validation — Defensible by Design | JCA Matrix™",
    "How every MarksMan recommendation is made defensible: official JCA and HTA guidance, AI-scale analysis, four gates of senior consultant validation, and full evidence traceability."
  );
  return (
    <>
      <PageHeader
        eyebrow="Trust & validation"
        title={<>Rigorous where it matters. <span className="text-blue-600">Confidential where it counts.</span></>}
        intro="How we ensure every recommendation is defensible — what we analyse, the guidance that anchors us, how AI and senior consultants work together, and the controls behind every engagement. The proprietary methods inside JCA Matrix™ remain confidential; the principles behind them do not."
      />

      {/* Evidence sources */}
      <section className="py-16" data-testid="evidence-sources-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Eyebrow>What we analyse</Eyebrow>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold tracking-tight max-w-2xl">
            Every source that shapes your assessment
          </h2>
          <p className="mt-5 max-w-2xl text-slate-600 leading-relaxed">
            An engagement looks at your evidence the way assessors will — in the context of everything published
            around it.
          </p>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {EVIDENCE_SOURCES.map((s, i) => (
              <motion.div key={s.title} variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
                className="rounded-2xl border border-slate-200 bg-white p-6 hover:-translate-y-1 hover:shadow-lg hover:shadow-slate-900/5 transition-all duration-300"
                data-testid={`evidence-source-${i}`}>
                <div className="h-10 w-10 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center">
                  <s.icon className="h-5 w-5 text-blue-600" strokeWidth={1.75} />
                </div>
                <h3 className="mt-3.5 font-display text-base font-semibold tracking-tight">{s.title}</h3>
                <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Guidance */}
      <section className="py-20 bg-slate-50 border-y border-slate-200" data-testid="guidance-section">
        <div className="max-w-6xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <Eyebrow>Our anchor</Eyebrow>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold tracking-tight">
              Anchored in official JCA and HTA guidance
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              We don't invent assessment criteria. Every analysis is grounded in the published rules, methods and
              precedents that will actually govern your assessment — updated continuously as new guidance and
              assessments are released.
            </p>
          </div>
          <div className="lg:col-span-7">
            <div className="rounded-2xl border border-slate-200 bg-white divide-y divide-slate-100 shadow-sm">
              {GUIDANCE.map((g, i) => (
                <motion.div key={g} variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
                  className="flex items-center gap-3 px-6 py-3.5" data-testid={`guidance-item-${i}`}>
                  <BookOpenCheck className="h-4 w-4 text-blue-600 shrink-0" />
                  <span className="text-sm font-medium text-slate-700">{g}</span>
                </motion.div>
              ))}
            </div>
            <p className="mt-4 text-xs text-slate-500 px-1" data-testid="alignment-disclaimer">
              This reflects methodological alignment with published frameworks — not regulatory certification or
              endorsement by any authority.
            </p>
          </div>
        </div>
      </section>

      {/* How AI supports */}
      <section className="py-24" data-testid="ai-support-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center max-w-2xl mx-auto">
            <Eyebrow>How AI supports the analysis</Eyebrow>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold tracking-tight">
              AI does the heavy lifting. <span className="text-blue-600">Never the judging.</span>
            </h2>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {AI_SUPPORT.map((a, i) => (
              <motion.div key={a.title} variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
                className="rounded-2xl border border-slate-200 bg-white p-6 hover:-translate-y-1 hover:shadow-lg hover:shadow-slate-900/5 transition-all duration-300"
                data-testid={`ai-support-${i}`}>
                <div className="h-10 w-10 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center">
                  <a.icon className="h-5 w-5 text-blue-600" strokeWidth={1.75} />
                </div>
                <h3 className="mt-3.5 font-display text-base font-semibold tracking-tight">{a.title}</h3>
                <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{a.desc}</p>
              </motion.div>
            ))}
          </div>
          <div className="mt-10 max-w-3xl mx-auto rounded-2xl bg-slate-900 px-6 sm:px-8 py-6 text-center" data-testid="ai-boundary">
            <Cpu className="h-5 w-5 text-blue-400 mx-auto" />
            <p className="mt-3 text-slate-200 font-medium leading-relaxed">
              AI accelerates and organizes the analysis. It never makes the recommendation.
              Strategic judgment — what the evidence means and what you should do — belongs to senior consultants.
            </p>
          </div>
        </div>
      </section>

      {/* Consultant validation */}
      <section className="py-24 bg-slate-50 border-y border-slate-200" data-testid="validation-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center max-w-2xl mx-auto">
            <Eyebrow>How consultants validate</Eyebrow>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold tracking-tight">
              Four gates between the analysis and your board
            </h2>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {VALIDATION_STEPS.map((v, i) => (
              <motion.div key={v.title} variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
                className="relative rounded-2xl border border-slate-200 bg-white p-6 hover:-translate-y-1 hover:shadow-lg hover:shadow-slate-900/5 transition-all duration-300"
                data-testid={`validation-step-${i}`}>
                <div className="absolute -top-3 -right-1 font-display text-6xl font-bold text-slate-100 select-none leading-none">{i + 1}</div>
                <div className="relative">
                  <div className="h-10 w-10 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center">
                    <v.icon className="h-5 w-5 text-blue-600" strokeWidth={1.75} />
                  </div>
                  <h3 className="mt-3.5 font-display text-base font-semibold tracking-tight">{v.title}</h3>
                  <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{v.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Traceability */}
      <section className="py-24 bg-slate-50 border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="text-center max-w-2xl mx-auto">
            <Eyebrow>Traceability</Eyebrow>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold tracking-tight">
              Ask "why?" about any conclusion — <span className="text-blue-600">we can show you</span>
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              Nothing in a MarksMan deliverable is asserted without a documented chain behind it. Every
              recommendation traces back through four layers:
            </p>
          </div>
          <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-5" data-testid="traceability-grid">
            {TRACE_CHAIN.map((t, i) => (
              <motion.div key={t.label} variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
                className="rounded-2xl border border-slate-200 bg-white p-6 text-center hover:-translate-y-1 hover:shadow-lg hover:shadow-slate-900/5 transition-all duration-300">
                <div className="mx-auto h-11 w-11 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center">
                  <t.icon className="h-5 w-5 text-blue-600" strokeWidth={1.75} />
                </div>
                <div className="mt-3.5 font-display text-sm font-semibold tracking-tight">{t.label}</div>
                <p className="mt-1.5 text-xs text-slate-500 leading-relaxed">{t.desc}</p>
              </motion.div>
            ))}
          </div>
          <p className="mt-8 text-center text-sm text-slate-500 max-w-2xl mx-auto">
            This matters twice: first when your board challenges a conclusion, and again when assessors do.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-x-8 gap-y-2" data-testid="qa-strip">
            {["Deliverables versioned — changes between issues documented", "Assumptions and limitations stated, never buried"].map((t) => (
              <span key={t} className="inline-flex items-center gap-2 text-sm text-slate-600">
                <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" /> {t}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Positioning statement */}
      <section className="py-16" data-testid="decision-support-statement">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <div className="rounded-2xl border-l-4 border-blue-600 border border-slate-200 bg-white px-7 sm:px-9 py-7 shadow-sm">
            <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">Our role, precisely</div>
            <p className="mt-3 text-slate-800 font-medium leading-relaxed">
              JCA Matrix™ is a strategic decision-support framework designed to strengthen JCA readiness, evidence
              planning and national HTA preparedness. It supports — but does not replace — formal regulatory and
              HTA assessment processes.
            </p>
            <p className="mt-3 text-sm text-slate-600 leading-relaxed">
              Final assessment outcomes rest with the responsible authorities. Our role is to make sure you arrive
              prepared — with the strongest defensible evidence position your data allows.
            </p>
          </div>
        </div>
      </section>

      {/* Signals + CTA */}
      <section className="py-20 bg-slate-900" data-testid="trust-signals">
        <div className="max-w-5xl mx-auto px-6 md:px-10 text-center">
          <div className="flex flex-wrap justify-center gap-x-7 gap-y-3">
            {SIGNALS.map((s) => (
              <span key={s} className="inline-flex items-center gap-2 text-sm font-medium text-slate-200">
                <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" /> {s}
              </span>
            ))}
          </div>
          <p className="mt-8 text-slate-400 max-w-2xl mx-auto text-sm leading-relaxed">
            The proprietary scoring, weighting and decision logic inside JCA Matrix™ is confidential intellectual
            property — shared with clients during engagements, under confidentiality.
          </p>
          <Button onClick={() => navigate("/contact")} data-testid="trust-cta"
            className="group mt-8 bg-blue-600 hover:bg-blue-700 text-white rounded-lg h-11 px-6 font-medium">
            Start a conversation <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
          </Button>
        </div>
      </section>
    </>
  );
}
